// healthcare.js
// Provides Massachusetts healthcare issue configuration, including bills, groups, and stories.
import allKids from '../../assets/issue_images/ma/healthcare/all_kids.jpg';
import connectorCare from '../../assets/issue_images/ma/healthcare/connector_care.jpg';
import dentalHealth from '../../assets/issue_images/ma/healthcare/dental_health.jpg';
import telehealth from '../../assets/issue_images/ma/healthcare/telehealth.jpg';

export const ISSUE_METADATA = {
  title: 'Healthcare advocacy in Massachusetts',
  summary:
    'Explore stories, bills, and advocacy groups working to improve access, affordability and quality of healthcare in Massachusetts.',
};

export const STORY_SCROLL_CONTENT = {
  topic: 'healthcare',
  heroContent: {
    preGradientText: 'Everyday people are improving',
    gradientText: 'healthcare access',
    postGradientText: '',
    description: 'Residents across Massachusetts are driving solutions that make care more affordable and inclusive.',
  },
  lastUpdated: 'November 2025',
};

export const BARRIER_STORY_CONTENT = [
  {
    "key": "connectorcare-expansion-2024",
    "title": "ConnectorCare Expansion",
    "caption": "Neighbors organized to expand ConnectorCare so more residents can afford coverage.",
    "details": [
      "Advocacy groups pressed for affordability and clear on-ramps; the Health Connector Board adopted the expansion after FY24 budget authorization.",
      "By August 2024, more than 51,000 people had enrolled under the pilot, many reporting using long-delayed preventive care and saving about $150 per month on premiums.",
      "The state has since reported over 50,000 pilot enrollees and issued public reports so residents can track progress."
    ],
    "takeaway": "Big change happens when neighbors come together",
    "imageUrl": connectorCare,
    "imageSource": "mahealthconnector.org",
    "sources": [
      "https://malegislature.gov/Bills/194/HD4892.pdf",
      "https://betterhealthconnector.com/wp-content/uploads/ConnectorCare-Pilot-Expansion-Report-082624.pdf",
      "https://hcfama.org/press-statement-health-care-for-all-celebrates-the-success-of-the-connector-care-expansion-pilot-program-august-28-2024/",
      "https://www.mahealthconnector.org/connectorcare2024"
    ]
  },
  {
    "key": "cover-all-kids-2024",
    "title": "Cover All Kids",
    "caption": "Families testified to win MassHealth coverage for every income-eligible child.",
    "details": [
      "Immigrant families, faith leaders, and advocates (led by Health Care For All) organized testimony and media stories to humanize the coverage gaps affecting kids.",
      "FY24 budget and subsequent regulatory steps enabled a pilot to extend affordable coverage pathways; legal aid groups published plain-language guides to help families enroll.",
      "Advocates estimated 31,000 to 48,000 children could newly qualify, grounding the push in concrete need and budget analysis.",
      "Local press highlighted individual parents testifying with their children, demonstrating that ordinary voices shaped the debate."
    ],
    "imageUrl": allKids,
    "imageSource": "hcfama.org",
    "takeaway": "One family's story can move a state to act",
    "sources": [
      "https://www.wgbh.org/news/politics/2023-07-25/bill-would-make-kids-without-legal-immigration-status-eligible-for-masshealth",
      "https://hcfama.org/policy_campaigns/cover-all-kids/",
      "https://www.masslegalservices.org/content/understanding-immigrants-eligibility-masshealth-other-subsidized-coverage-march-2025"
    ]
  },  
  {
    "key": "telehealth-patients-first-2020",
    "title": "Patients First",
    "caption": "Patients and clinicians secured the Patients First law to keep telehealth coverage permanent.",
    "details": [
      "During COVID, residents shared how virtual visits kept them in care; lawmakers codified broad telehealth coverage and payment parity for behavioral health in Chapter 260 (2020).",
      "The law established a comprehensive framework across state-regulated plans and MassHealth, removing many pre-pandemic barriers to virtual care.",
      "State agencies and stakeholder testimony translated personal experiences into durable rules, with follow-on reports and guidance to refine access and affordability.",
      "Result: easier access for people with transportation, work, or caregiving constraints, and continued parity for behavioral health visits."
    ],
    "takeaway": "You don't have to be a lobbyist to break through",
    "imageUrl": telehealth,
    "imageSource": "massmed.org",
    "sources": [
      "https://malegislature.gov/Laws/SessionLaws/Acts/2020/Chapter260",
      "https://www.massmed.org/Practice-Support/Telehealth-and-Virtual-Care/Telehealth-and-Virtual-Care/",
      "https://masshpc.gov/sites/default/files/2023-04/Telehealth%20Use%20in%20the%20Commonwealth%20and%20Policy%20Recommendations_0.pdf",
      "https://www.wgbh.org/news/local/2020-12-23/telemedicine-here-to-stay-in-massachusetts-proponents-say"
    ]
  },
  {
    "key": "adult-dental-restored-2021",
    "title": "Restoring Adult Dental Benefits",
    "caption": "Persistent testimony restored full adult dental benefits for MassHealth members.",
    "details": [
      "Coalitions of patients, community health centers, and oral-health advocates kept up annual budget advocacy, letters, and hearings.",
      "Fiscal Year 2021 changes completed the restoration; subsequent state materials confirm comprehensive adult dental coverage for MassHealth members.",
      "Policy makers cited reduced ER visits and better health from preventive and restorative care when weighing restoration.",
      "Advocacy groups documented that over one million residents rely on MassHealth, making restored dental coverage a high-impact affordability win."
    ],
    "takeaway": "One person's outreach can tip the balance",
    "imageUrl": dentalHealth,
    "imageSource": "hcfama.org",
    "sources": [
      "https://hcfama.org/finally-oral-health-benefits-have-been-fully-restored-in-masshealth/",
      "https://www.massdental.org/professional-resources/insurance-and-coding/masshealth-overview/",
      "https://masshpc.gov/publications/datapoints-series/issue-20-oral-health-access-and-equity-commonwealth",
      "https://budget.digital.mass.gov/govbudget/fy20/outside-section/section-76-mass-health-dental-coverage"
    ]
  }
]


export const BILLS = [
  {
    id: 'cover-all-kids',
    title: 'Cover All Kids Act',
    numbers: {
      house: { label: 'H1403', url: 'https://malegislature.gov/Bills/194/H1403' },
      senate: { label: 'S855', url: 'https://malegislature.gov/Bills/194/S855' },
    },
    short_summary: 'Expands health coverage for children regardless of immigration status.',
    short_summary_gerund: 'Expanding health coverage for children regardless of immigration status.',
    why: 'If passed, this bill would ensure all children have access to necessary health care services.',
    bullets: [
      'Provides MassHealth benefits for eligible children',
      'Covers young adults under 21 years old',
      'Ensures comprehensive health coverage regardless of immigration status',
    ],
    supporters: [
      {
        name: 'Health Care For All (Mass.)',
        joinUrl: 'https://hcfama.org',
        infoUrl: 'https://hcfama.org',
      },
    ],
  },
  {
    id: 'medical-debt-relief',
    title: 'Medical Debt Relief & Health Safety Net',
    numbers: {
      house: { label: 'H419', url: 'https://malegislature.gov/Bills/194/H419' },
      senate: { label: 'S214', url: 'https://malegislature.gov/Bills/194/S214' },
    },
    short_summary: 'Alleviates medical debt burden for patients and families.',
    short_summary_gerund: 'Alleviating medical debt burden for patients and families.',
    why: 'If passed, this bill would help families manage medical debt more effectively.',
    bullets: [
      'Prohibits selling medical debt to debt buyers',
      'Limits collection actions for medical debts',
      'Exempts certain income and property from seizure',
    ],
    supporters: [
      {
        name: 'Health Care For All (Mass.)',
        joinUrl: 'https://hcfama.org',
        infoUrl: 'https://hcfama.org',
      },
    ],
  },
  {
    id: 'medicare-for-all-ma',
    title: '“Medicare for All” — MA Health Care Trust',
    numbers: {
      house: { label: 'H1405', url: 'https://malegislature.gov/Bills/194/H1405' },
      senate: { label: 'S860', url: 'https://malegislature.gov/Bills/194/S860' },
    },
    short_summary: 'Establishes Medicare for All in Massachusetts',
    short_summary_gerund: 'Establishing Medicare for All in Massachusetts',
    why: 'If passed, this bill would provide equitable healthcare access for all Massachusetts residents, regardless of their financial situation.',
    bullets: [
      'Guarantees healthcare access for all residents',
      'Eliminates out-of-pocket costs for services',
      'Funds healthcare through dedicated taxes',
    ],
    supporters: [
      {
        name: 'Mass-Care (Mass. Campaign for Single Payer)',
        joinUrl: 'https://www.masscare.org',
        infoUrl: 'https://www.masscare.org',
      },
    ],
  },
  {
    id: 'supportive-care-serious-mental-illness',
    title: 'Supportive Care for Serious Mental Illness',
    numbers: {
      house: { label: 'H1135', url: 'https://malegislature.gov/Bills/194/H1135' },
      senate: { label: 'S709', url: 'https://malegislature.gov/Bills/194/S709' },
    },
    short_summary: 'Expands mental health care coverage for serious conditions.',
    short_summary_gerund: 'Expanding mental health care coverage for serious conditions.',
    why: 'If passed, this bill would enhance access to critical mental health services for individuals in need.',
    bullets: [
      'Requires insurance to cover specialized mental health services',
      'Supports treatment for first-episode psychosis',
      'Ensures no visit limits for covered services',
    ],
    supporters: [
      {
        name: 'NAMI Massachusetts',
        joinUrl: 'https://namimass.org',
        infoUrl: 'https://namimass.org',
      },
    ],
  },
  {
    id: 'improve-autism-idd-care',
    title: 'Improve Autism/IDD Care — Provider Training',
    numbers: {
      house: { label: 'H242', url: 'https://malegislature.gov/Bills/194/H242' },
      senate: { label: 'S149', url: 'https://malegislature.gov/Bills/194/S149' },
    },
    short_summary: 'Enhances care standards for autism and developmental disabilities.',
    short_summary_gerund: 'Enhancing care standards for autism and developmental disabilities.',
    why: 'If passed, this bill would enhance healthcare for individuals with disabilities and their families.',
    bullets: [
      'Establishes training for healthcare providers',
      'Creates an advisory committee for standards',
      'Improves diagnosis during routine check-ups',
    ],
    supporters: [
      {
        name: 'The Arc of Massachusetts',
        joinUrl: 'https://thearcofmass.org',
        infoUrl: 'https://thearcofmass.org',
      },
    ],
  },
  {
    id: 'abortion-care-for-young-people',
    title: 'Abortion Care for Young People',
    numbers: {
      house: { label: 'H1991', url: 'https://malegislature.gov/Bills/194/H1991' },
      senate: { label: 'S1579', url: 'https://malegislature.gov/Bills/194/S1579' },
    },
    short_summary: 'Regulates abortion care access for young people.',
    short_summary_gerund: 'Regulating abortion care access for young people.',
    why: "If passed, this bill would enhance young people's access to necessary healthcare services.",
    bullets: [
      'Allows minors to consent to abortion care',
      'Ensures confidentiality of consent forms',
      'Mandates informed consent before abortion procedures',
    ],
    supporters: [
      {
        name: 'Planned Parenthood Advocacy Fund of MA',
        joinUrl: 'https://www.plannedparenthoodaction.org/planned-parenthood-advocacy-fund-massachusetts-inc',
        infoUrl: 'https://www.plannedparenthoodaction.org/planned-parenthood-advocacy-fund-massachusetts-inc',
      },
    ],
  },
  {
    id: 'protective-gear-anti-discrimination',
    title: 'Protective Gear Anti-Discrimination',
    numbers: {
      house: { label: 'H1981', url: 'https://malegislature.gov/Bills/194/H1981' },
      senate: { label: 'S1242', url: 'https://malegislature.gov/Bills/194/S1242' },
    },
    short_summary: 'Protects individuals from discrimination for wearing protective gear.',
    short_summary_gerund: 'Protecting individuals from discrimination for wearing protective gear.',
    why: "If passed, this bill would protect individuals' rights to wear safety gear without fear of discrimination.",
    bullets: [
      'Prohibits discrimination for wearing protective medical equipment',
      'Covers face masks, gloves, and other safety gear',
      'Protects employees from termination for wearing gear',
    ],
    supporters: [
      {
        name: 'Mass. Public Health Association',
        joinUrl: 'https://mapublichealth.org',
        infoUrl: 'https://mapublichealth.org',
      },
    ],
  },
  {
    id: 'wheelchair-repair-bill',
    title: 'Warranty Protections for Wheelchairs (“Wheelchair Repair Bill”)',
    numbers: {
      house: { label: 'H1278', url: 'https://malegislature.gov/Bills/194/H1278' },
      senate: { label: 'S210', url: 'https://malegislature.gov/Bills/194/S210' },
    },
    short_summary: 'Establishes wheelchair repair requirements and consumer protections.',
    short_summary_gerund: 'Establishing wheelchair repair requirements and consumer protections.',
    why: 'If passed, this bill would enhance mobility and support for wheelchair users.',
    bullets: [
      'Requires timely repairs for wheelchairs within ten business days',
      'Mandates home repairs for complex rehabilitation wheelchairs',
      'Eliminates preauthorization for repairs if prescriptions are recent',
    ],
    supporters: [
      {
        name: 'Dignity Alliance Massachusetts',
        joinUrl: 'https://dignityalliancema.org',
        infoUrl: 'https://dignityalliancema.org',
      },
    ],
  },
  {
    id: 'patient-safety-act',
    title: 'Patient Safety Act — Safe Nurse-to-Patient Staffing',
    numbers: {
      house: { label: 'H2448', url: 'https://malegislature.gov/Bills/194/H2448' },
      senate: { label: 'S1522', url: 'https://malegislature.gov/Bills/194/S1522' },
    },
    short_summary: 'Establishes patient limits for registered nurses in hospitals.',
    short_summary_gerund: 'Establishing patient limits for registered nurses in hospitals.',
    why: "If passed, this bill would help ensure safer patient care and support nurses' working conditions.",
    bullets: [
      'Sets maximum patient assignments for registered nurses',
      'Includes enforcement mechanisms for violations',
      'Protects nurses from retaliation for compliance',
    ],
    supporters: [
      {
        name: 'Massachusetts Nurses Association',
        joinUrl: 'https://massnurses.org',
        infoUrl: 'https://massnurses.org',
      },
    ],
  },
  {
    id: 'advance-health-equity',
    title: 'Act to Advance Health Equity',
    numbers: {
      house: { label: 'H1416', url: 'https://malegislature.gov/Bills/194/H1416' },
      senate: { label: 'S901', url: 'https://malegislature.gov/Bills/194/S901' },
    },
    short_summary: 'Establishes an executive office for health equity in Massachusetts.',
    short_summary_gerund: 'Establishing an executive office for health equity in Massachusetts.',
    why: 'If passed, this bill would help reduce health disparities affecting marginalized communities.',
    bullets: [
      'Creates an executive office of equity',
      'Forms a special commission on health inequities',
      'Develops benchmarks to reduce health disparities',
    ],
    supporters: [
      {
        name: 'Health Equity Compact',
        joinUrl: 'https://healthequitycompact.org',
        infoUrl: 'https://healthequitycompact.org',
      },
    ],
  },
];

export const GROUPS = [
  {
    name: 'Health Care For All (Mass.)',
    joinUrl: 'https://hcfama.org',
    summary: 'Statewide nonprofit pushing for equitable coverage and health justice in Massachusetts.',
    slug: 'health-care-for-all-mass',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['cover-all-kids', 'medical-debt-relief'],
  },
  {
    name: 'Mass-Care (Mass. Campaign for Single Payer)',
    joinUrl: 'https://masscare.org',
    summary: 'Grassroots coalition campaigning to bring single-payer health care to Massachusetts.',
    slug: 'mass-care',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['medicare-for-all-ma'],
  },
  {
    name: 'NAMI Massachusetts',
    joinUrl: 'https://namimass.org',
    summary: 'Grassroots network of people with mental health conditions and families improving care.',
    slug: 'nami-massachusetts',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['supportive-care-serious-mental-illness'],
  },
  {
    name: 'The Arc of Massachusetts',
    joinUrl: 'https://thearcofmass.org',
    summary: 'Statewide advocates supporting people with autism and developmental disabilities and their families.',
    slug: 'the-arc-of-massachusetts',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['improve-autism-idd-care'],
  },
  {
    name: 'Planned Parenthood Advocacy Fund of MA',
    joinUrl: 'https://www.plannedparenthoodaction.org/planned-parenthood-advocacy-fund-massachusetts-inc',
    summary: 'Advocacy arm of Planned Parenthood League of MA advancing reproductive health access.',
    slug: 'planned-parenthood-advocacy-fund-ma',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['abortion-care-for-young-people'],
  },
  {
    name: 'Mass. Public Health Association',
    joinUrl: 'https://mapublichealth.org',
    summary: 'Policy and organizing nonprofit working for a healthier Massachusetts.',
    slug: 'massachusetts-public-health-association',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['protective-gear-anti-discrimination'],
  },
  {
    name: 'Dignity Alliance Massachusetts',
    joinUrl: 'https://dignityalliancema.org',
    summary: 'Statewide coalition defending the rights and dignity of older adults and disabled residents.',
    slug: 'dignity-alliance-massachusetts',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['wheelchair-repair-bill'],
  },
  {
    name: 'Massachusetts Nurses Association',
    joinUrl: 'https://massnurses.org',
    summary: "Largest union of RNs in MA advancing nursing standards and supporting nurses' rights.",
    slug: 'massachusetts-nurses-association',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['patient-safety-act'],
  },
  {
    name: 'Health Equity Compact',
    joinUrl: 'https://healthequitycompact.org',
    summary: 'Coalition of leaders of color pressing for structural solutions to health inequities.',
    slug: 'health-equity-compact',
    logoUrl: 'https://placehold.co/64x64?text=Logo',
    supports: ['advance-health-equity'],
  },
];
